import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Token storage: we use sessionStorage (instead of localStorage) as a fallback
// for environments where 3rd-party cookies are blocked (in-app webviews, Safari ITP).
// sessionStorage limits the token to the current tab and reduces XSS blast radius
// vs. localStorage. The httpOnly cookie remains the primary auth mechanism on
// regular browsers; sessionStorage is just a graceful-degradation path.
const TOKEN_KEY = "session_token";

function safeGetToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || null;
  } catch (err) {
    console.warn("[api] sessionStorage read failed:", err?.message);
    return null;
  }
}

export function storeToken(token) {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch (err) {
    console.warn("[api] sessionStorage write failed:", err?.message);
  }
}

export function clearStoredToken() {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch (err) {
    console.warn("[api] sessionStorage clear failed:", err?.message);
  }
}

api.interceptors.request.use((config) => {
  const tok = safeGetToken();
  if (tok && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearStoredToken();
    }
    return Promise.reject(err);
  }
);

export default api;

export const SPORTS = [
  { id: "futsal", label: "Futsal", icon: "⚽", color: "#FF6B35" },
  { id: "padel", label: "Padel", icon: "🎾", color: "#00E5FF" },
  { id: "badminton", label: "Badminton", icon: "🏸", color: "#A78BFA" },
  { id: "tenis", label: "Tenis", icon: "🎾", color: "#FBBF24" },
  { id: "basket", label: "Basket", icon: "🏀", color: "#F97316" },
  { id: "sepakbola", label: "Sepak Bola", icon: "⚽", color: "#10B981" },
  { id: "gym", label: "Gym", icon: "🏋️", color: "#EF4444" },
  { id: "lari", label: "Lari", icon: "🏃", color: "#06B6D4" },
];

// Optional position taxonomy per sport. Sports not listed = no positions UI.
export const SPORT_POSITIONS = {
  futsal: [
    { id: "field", label: "Field Player", short: "FP" },
    { id: "goalkeeper", label: "Goalkeeper", short: "GK" },
  ],
  basket: [
    { id: "pg", label: "Point Guard", short: "PG" },
    { id: "sg", label: "Shooting Guard", short: "SG" },
    { id: "sf", label: "Small Forward", short: "SF" },
    { id: "pf", label: "Power Forward", short: "PF" },
    { id: "c", label: "Center", short: "C" },
  ],
  sepakbola: [
    { id: "gk", label: "Goalkeeper", short: "GK" },
    { id: "def", label: "Defender", short: "DEF" },
    { id: "mid", label: "Midfielder", short: "MID" },
    { id: "fwd", label: "Forward", short: "FWD" },
  ],
};

export function positionInfo(sportId, posId) {
  const list = SPORT_POSITIONS[sportId] || [];
  return list.find((p) => p.id === posId) || null;
}

export const CITIES = [
  "Jakarta", "Bandung", "Surabaya", "Yogyakarta", "Bali (Denpasar)",
  "Semarang", "Medan", "Makassar", "Tangerang", "Bekasi", "Depok", "Bogor",
];

export const SKILL_LEVELS = [
  { id: "all", label: "Semua Level" },
  { id: "beginner", label: "Pemula" },
  { id: "intermediate", label: "Menengah" },
  { id: "advanced", label: "Mahir" },
];

export function sportInfo(id) {
  return SPORTS.find(s => s.id === id) || { id, label: id, icon: "🏅", color: "#A1A1AA" };
}

export function formatRupiah(n) {
  if (!n) return "Gratis";
  return "Rp " + Number(n).toLocaleString("id-ID");
}

// Normalize Indonesian WhatsApp number to international `62xxx` format (no plus sign).
// Accepts inputs like: `0812...`, `+62812...`, `62812...`, `812...`, with spaces or dashes.
// Returns "" if input is empty/invalid.
export function normalizeWa(raw) {
  let digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("0")) digits = digits.slice(1);
  if (digits.startsWith("62")) digits = digits.slice(2);
  return "62" + digits;
}

export function formatMatchTime(iso) {
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const time = d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
  if (isToday) return `Hari ini · ${time}`;
  if (isTomorrow) return `Besok · ${time}`;
  return d.toLocaleDateString("id-ID", { weekday: "short", day: "numeric", month: "short" }) + " · " + time;
}
